type FileValidationOptions<TFieldName extends string> = {
  maxSizeMB?: number;
  allowedTypes?: string[];
  fieldName: TFieldName;
  setValue: (
    name: TFieldName,
    value: File | null,
    options?: { shouldValidate?: boolean }
  ) => void;
  setError: (
    name: TFieldName,
    error: { type: string; message: string }
  ) => void;
  clearErrors: (name: TFieldName) => void;
  trigger: (name: TFieldName) => Promise<boolean>;
  setPreview?: (preview: string | null) => void;
};

export const createFileChangeHandler = <TFieldName extends string>({
  maxSizeMB = 5,
  allowedTypes = ["image/jpeg", "image/png", "application/pdf"],
  fieldName,
  setValue,
  setError,
  clearErrors,
  trigger,
  setPreview,
}: FileValidationOptions<TFieldName>) => {
  return async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;

    clearErrors(fieldName);
    setPreview?.(null);

    if (!file) return;

    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(fieldName, {
        type: "fileSize",
        message: `File size should not exceed ${maxSizeMB}MB`,
      });
      e.target.value = "";
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setError(fieldName, {
        type: "fileType",
        message: `Invalid file type. Allowed types: ${allowedTypes.join(", ")}`,
      });
      e.target.value = "";
      return;
    }

    setValue(fieldName, file, { shouldValidate: true });
    await trigger(fieldName);
  };
};
